import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { VerseBanner } from "@/components/VerseBanner";
import { VoicePicker } from "@/components/VoicePicker";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { playBadge } from "@/lib/sfx";
import { Clock, Play, RotateCw, CheckCircle2, BookHeart, ArrowRight, CalendarDays, BookOpen, Bell, Award, ListChecks, BookMarked, Gamepad2, Trophy } from "lucide-react";

const SUBJECT_COLOR = { Science: "#81B29A", English: "#E07A5F", Bible: "#F4A261", Math: "#A8DADC", "Social Studies": "#6B7280" };

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [badgeCount, setBadgeCount] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showPastDue, setShowPastDue] = useState(false);
  const [showCurrent, setShowCurrent] = useState(true);
  const [showUpcoming, setShowUpcoming] = useState(true);

  useEffect(() => { api.get("/my/assignments").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/badges/${user.id}`).then((r) => {
      setBadgeCount(r.data.earned_count);
      const earnedIds = r.data.badges.filter((b) => b.earned).map((b) => b.id);
      const seen = JSON.parse(localStorage.getItem("rfa_badges") || "[]");
      const fresh = r.data.badges.filter((b) => b.earned && !seen.includes(b.id));
      if (fresh.length > 0) playBadge();
      fresh.forEach((b) => toast.success(`🏅 Badge earned: ${b.name}!`, { description: b.desc }));
      localStorage.setItem("rfa_badges", JSON.stringify(earnedIds));
    });
  }, [user]);

  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  const todayStr = ymd(new Date());
  const tmr = new Date(); tmr.setDate(tmr.getDate() + 1);
  const tmrStr = ymd(tmr);
  const reminders = items.filter((a) => a.status !== "Graded" && a.due_date && [todayStr, tmrStr].includes(a.due_date.slice(0, 10)))
    .map((a) => ({ ...a, when: a.due_date.slice(0, 10) === todayStr ? "today" : "tomorrow" }));

  const cta = (status) => {
    if (status === "Not Started") return { label: "Start", icon: Play };
    if (status === "In Progress") return { label: "Continue", icon: RotateCw };
    return { label: "Review", icon: CheckCircle2 };
  };

  // Week buckets, anchored to the Monday of the week containing today.
  const fmtDay = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const now = new Date();
  const dow = now.getDay(); // 0 Sun .. 6 Sat
  const weekStart = new Date(now); weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(now.getDate() + (dow === 0 ? -6 : 1 - dow)); // Monday
  const weekFriday = new Date(weekStart); weekFriday.setDate(weekStart.getDate() + 4);
  const nextWeekStart = new Date(weekStart); nextWeekStart.setDate(weekStart.getDate() + 7);
  const dueOf = (a) => (a.due_date ? new Date(a.due_date.slice(0, 10) + "T00:00:00") : null);
  const isFinished = (s) => s === "Graded" || s === "Needs Review";
  const sortByDue = (x, y) => (x.due_date || "").localeCompare(y.due_date || "") || x.subject.localeCompare(y.subject);

  const unfinishedItems = items.filter((a) => !isFinished(a.status));
  const completed = items.filter((a) => isFinished(a.status)).sort(sortByDue);
  const pastDue = unfinishedItems.filter((a) => { const d = dueOf(a); return d && d < weekStart; }).sort(sortByDue);
  const current = unfinishedItems.filter((a) => { const d = dueOf(a); return !d || (d >= weekStart && d < nextWeekStart); }).sort(sortByDue);
  const future = unfinishedItems.filter((a) => { const d = dueOf(a); return d && d >= nextWeekStart; }).sort(sortByDue);

  const AssignmentCard = ({ a, i }) => {
    const c = cta(a.status); const color = SUBJECT_COLOR[a.subject] || "#81B29A";
    return (
      <motion.div key={a.id} data-testid={`assignment-${a.id}`}
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.06, 0.4) }}
        className="rounded-2xl bg-white border border-[#EAE6DF] p-6 flex items-center justify-between gap-4 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow]">
        <div className="flex items-center gap-4">
          <span className="w-1.5 h-14 rounded-full" style={{ background: color }} />
          <div>
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color }}>{a.subject}</span>
            <p className="font-head font-semibold text-lg text-[#3D405B]">{a.title}</p>
            <p className="text-xs text-[#6B7280] flex items-center gap-1"><Clock size={12} /> ~{a.estimated_minutes || 25} min
              {a.due_date && <span className="ml-2">· Due {new Date(a.due_date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>}
              {a.status === "In Progress" && <span className="stat text-[#F4A261] ml-2">In progress</span>}
              {a.status === "Graded" && <span className="stat text-[#81B29A] ml-2">Scored {a.percentage}%</span>}
              {a.status === "Needs Review" && <span className="stat text-[#F4A261] ml-2">Submitted · awaiting review</span>}</p>
          </div>
        </div>
        <button data-testid={`open-assignment-${a.id}`} onClick={() => navigate(`/student/lesson/${a.id}`)}
          className="flex items-center gap-2 rounded-full text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105" style={{ background: color }}>
          <c.icon size={16} /> {c.label}
        </button>
      </motion.div>
    );
  };

  const Group = ({ testid, title, subtitle, accent, dot, list, open, onToggle }) => (
    list.length === 0 ? null : (
      <section data-testid={testid} className="mb-8">
        <button data-testid={`toggle-${testid.replace("group-", "")}`} onClick={onToggle}
          className="flex items-center gap-2.5 mb-3 w-full text-left">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: dot }} />
          <h3 className="font-head text-base font-semibold" style={{ color: accent }}>{title}</h3>
          <span className="text-xs font-semibold text-[#6B7280] bg-[#F4F1EC] rounded-full px-2 py-0.5" data-testid={`${testid}-count`}>{list.length}</span>
          {subtitle && <span className="text-xs text-[#9AA0A6] ml-1">{subtitle}</span>}
          <span className="text-xs text-[#9AA0A6]">· {open ? "Hide" : "Show"}</span>
        </button>
        {open && <div className="space-y-4">{list.map((a, i) => <AssignmentCard key={a.id} a={a} i={i} />)}</div>}
      </section>
    )
  );

  return (
    <div>
      <TopBar subtitle="Student" />
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12">
        <div className="relative grain rounded-3xl overflow-hidden mb-10">
          <img src="https://images.unsplash.com/photo-1771627278482-dec14f5eda58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxzdHVkZW50cyUyMHN0dWR5aW5nJTIwbmF0dXJlfGVufDB8fHx8MTc4NjQ5MjM1NHww&ixlib=rb-4.1.0&q=85"
            alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3D405B]/80 to-[#3D405B]/20" />
          <div className="relative p-10 text-white">
            <p className="text-white/80 text-sm">{today}</p>
            <h1 className="font-head text-4xl md:text-5xl font-bold mt-1">Good morning, {user?.first_name}!</h1>
            <p className="mt-2 text-white/85">You have {unfinishedItems.length} {unfinishedItems.length === 1 ? "assignment" : "assignments"} to finish.</p>
            <button data-testid="student-guide-btn" onClick={() => navigate("/student/guide")}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/20 hover:bg-white/30 text-white px-5 py-2 text-sm font-semibold transition-colors backdrop-blur">
              ✨ New here? See how it works
            </button>
            <button data-testid="student-manual-btn" onClick={() => navigate("/student/manual")}
              className="mt-4 ml-3 inline-flex items-center gap-2 rounded-full bg-white/20 hover:bg-white/30 text-white px-5 py-2 text-sm font-semibold transition-colors backdrop-blur">
              <BookMarked size={15} /> Student Manual
            </button>
          </div>
        </div>

        <div className="mb-8"><VerseBanner tone="student" /></div>

        {reminders.length > 0 && (
          <motion.div data-testid="reminders-banner" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-8 rounded-2xl border border-[#F4A261]/40 bg-[#F4A261]/10 p-5">
            <p className="flex items-center gap-2 font-head font-semibold text-[#3D405B] mb-2"><Bell size={16} className="text-[#F4A261]" /> Reminders</p>
            <div className="space-y-1.5">
              {reminders.map((a) => (
                <button key={a.id} data-testid={`reminder-${a.id}`} onClick={() => navigate(`/student/lesson/${a.id}`)}
                  className="w-full flex items-center justify-between text-sm text-[#3D405B] hover:text-[#E07A5F] transition-colors">
                  <span><b>{a.subject}</b> — {a.title}</span>
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full text-white ${a.when === "today" ? "bg-[#E07A5F]" : "bg-[#F4A261]"}`}>Due {a.when}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button data-testid="student-badges-btn" onClick={() => navigate("/student/badges")}
            className="w-full flex items-center justify-between rounded-2xl bg-white border border-[#EAE6DF] p-5 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow]">
            <span className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-2xl bg-[#F4A261] text-white"><Award size={18} /></span>
              <span className="text-left"><span className="block font-head font-semibold text-[#3D405B]">My Badges</span><span className="block text-xs text-[#6B7280]">{badgeCount != null ? `${badgeCount} earned` : "Collect them all"}</span></span>
            </span>
          </button>
          <button data-testid="student-scripture-btn" onClick={() => navigate("/student/scripture")}
            className="w-full flex items-center justify-between rounded-2xl bg-white border border-[#EAE6DF] p-5 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow]">
            <span className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-2xl bg-[#81B29A] text-white"><BookHeart size={18} /></span>
              <span className="text-left"><span className="block font-head font-semibold text-[#3D405B]">Scripture Memory</span><span className="block text-xs text-[#6B7280]">Verses & streak</span></span>
            </span>
          </button>
          <button data-testid="student-reading-plan-btn" onClick={() => navigate("/student/reading-plan")}
            className="w-full flex items-center justify-between rounded-2xl bg-white border border-[#EAE6DF] p-5 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow]">
            <span className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-2xl bg-[#3D405B] text-white"><BookOpen size={18} /></span>
              <span className="text-left"><span className="block font-head font-semibold text-[#3D405B]">Reading Plan</span><span className="block text-xs text-[#6B7280]">Daily Bible reading</span></span>
            </span>
          </button>
          <button data-testid="student-calendar-btn" onClick={() => navigate("/student/calendar")}
            className="w-full flex items-center justify-between rounded-2xl bg-white border border-[#EAE6DF] p-5 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow]">
            <span className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-2xl bg-[#E07A5F] text-white"><CalendarDays size={18} /></span>
              <span className="text-left"><span className="block font-head font-semibold text-[#3D405B]">Calendar</span><span className="block text-xs text-[#6B7280]">What's coming up</span></span>
            </span>
          </button>
          <button data-testid="student-chores-btn" onClick={() => navigate("/student/chores")}
            className="w-full flex items-center justify-between rounded-2xl bg-white border border-[#EAE6DF] p-5 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow]">
            <span className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-2xl bg-[#F4A261] text-white"><ListChecks size={18} /></span>
              <span className="text-left"><span className="block font-head font-semibold text-[#3D405B]">Chores</span><span className="block text-xs text-[#6B7280]">Check them off</span></span>
            </span>
          </button>
          <button data-testid="student-games-btn" onClick={() => navigate("/student/games")}
            className="w-full flex items-center justify-between rounded-2xl bg-white border border-[#EAE6DF] p-5 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow]">
            <span className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-2xl bg-[#81B29A] text-white"><Gamepad2 size={18} /></span>
              <span className="text-left"><span className="block font-head font-semibold text-[#3D405B]">Mini-Games</span><span className="block text-xs text-[#6B7280]">Play & learn</span></span>
            </span>
          </button>
          <button data-testid="student-leaderboard-btn" onClick={() => navigate("/student/leaderboard")}
            className="w-full flex items-center justify-between rounded-2xl bg-white border border-[#EAE6DF] p-5 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow]">
            <span className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-2xl bg-[#E07A5F] text-white"><Trophy size={18} /></span>
              <span className="text-left"><span className="block font-head font-semibold text-[#3D405B]">Leaderboard</span><span className="block text-xs text-[#6B7280]">This week's stars</span></span>
            </span>
          </button>
        </div>

        <h2 className="font-head text-lg font-semibold text-[#3D405B] mb-4">Your assignments</h2>
        {loading ? <p className="text-[#6B7280]">Loading…</p> : items.length === 0 ? (
          <p className="text-[#6B7280]">Nothing assigned yet — check back soon!</p>
        ) : (
          <div data-testid="assignment-groups">
            <Group testid="group-past-due" title="Past Due" subtitle="from earlier weeks"
              accent="#E07A5F" dot="#E07A5F" list={pastDue} open={showPastDue} onToggle={() => setShowPastDue((v) => !v)} />
            <Group testid="group-current" title="This Week" subtitle={`${fmtDay(weekStart)} – ${fmtDay(weekFriday)}`}
              accent="#3D405B" dot="#81B29A" list={current} open={showCurrent} onToggle={() => setShowCurrent((v) => !v)} />
            <Group testid="group-future" title="Upcoming" subtitle="future weeks"
              accent="#6B7280" dot="#A8DADC" list={future} open={showUpcoming} onToggle={() => setShowUpcoming((v) => !v)} />

            {unfinishedItems.length === 0 && (
              <p data-testid="all-done-note" className="text-[#6B7280] mb-8">🎉 You're all caught up — no assignments to finish right now!</p>
            )}

            <Group testid="group-completed" title="Completed" subtitle={null}
              accent="#81B29A" dot="#81B29A" list={completed} open={showCompleted} onToggle={() => setShowCompleted((v) => !v)} />
          </div>
        )}

        <div className="mt-10"><VoicePicker /></div>
      </main>
    </div>
  );
}
